import { ChangeDetectionStrategy, Component, DestroyRef, OnDestroy, OnInit, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin, finalize, Subscription, switchMap, timer } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { SelectModule } from 'primeng/select';
import { ImageModule } from 'primeng/image';
import { DialogModule } from 'primeng/dialog';
import { TextareaModule } from 'primeng/textarea';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { PermissionService } from 'src/app/security/permission.service';
import { app_permissions } from 'src/app/security/app-permissions';
import { PageDataPanelComponent } from 'src/app/shared/page-layout/page-data-panel.component';
import { PosmProject } from '../../models/posm-project.model';
import {
    PosmExecutionCompletionDeliveryEvidence,
    PosmCompletionEvidenceReviewHistory,
    PosmExecutionCompletionReport,
    PosmExecutionCompletionReportJob,
    PosmExecutionCompletionReportSummary
} from '../../models/posm-execution.models';
import { PosmService } from '../../posm.service';
import { PendingActionTracker } from '../../../shared/state/pending-action-tracker';
import { completionEvidenceKey, groupCompletionEvidence } from './posm-completion-evidence-groups';

@Component({
    selector: 'app-posm-execution-completion-reports',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        CheckboxModule,
        TableModule,
        TagModule,
        SelectModule,
        ImageModule,
        DialogModule,
        TextareaModule,
        PageDataPanelComponent
    ],
    templateUrl: './posm-execution-completion-reports.component.html',
    styleUrl: './posm-execution-completion-reports.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PosmExecutionCompletionReportsComponent implements OnInit, OnDestroy {
    readonly project = input.required<PosmProject>();

    private readonly posmService = inject(PosmService);
    private readonly session = inject(SessionStorageService);
    private readonly permissions = inject(PermissionService);
    private readonly destroyRef = inject(DestroyRef);

    canReconcileTasks(): boolean {
        return this.permissions.hasPermission(app_permissions.posm.projects.edit);
    }

    readonly summary = signal<PosmExecutionCompletionReportSummary | null>(null);
    readonly reports = signal<PosmExecutionCompletionReport[]>([]);
    readonly deliveryEvidence = signal<PosmExecutionCompletionDeliveryEvidence[]>([]);
    readonly loading = signal(false);
    readonly generating = signal(false);
    readonly generationJob = signal<PosmExecutionCompletionReportJob | null>(null);
    private jobPolling?: Subscription;
    readonly reconcilingTasks = signal(false);
    readonly reportActions = new PendingActionTracker<string>();
    readonly reviewingEvidenceKey = signal<string | null>(null);
    readonly error = signal<string | null>(null);
    readonly message = signal<string | null>(null);
    readonly locationGroups = computed(() => groupCompletionEvidence(this.deliveryEvidence()));
    readonly unmappedEvidence = computed(() => this.deliveryEvidence().filter(e => !e.deliveryScheduleId || e.mappingStatus !== 'MAPPED'));
    readonly expandedLocations = signal(new Set<string>());
    readonly expandedSchedules = signal(new Set<string>());
    readonly unavailableImages = signal(new Set<string>());
    readonly reviewHistory = signal<PosmCompletionEvidenceReviewHistory[]>([]);
    readonly historyLoading = signal(false);
    readonly historyError = signal<string | null>(null);
    readonly historyAsset = signal<PosmExecutionCompletionDeliveryEvidence | null>(null);
    reviewDialogVisible = false;
    historyDialogVisible = false;
    reviewDraft: { evidence: PosmExecutionCompletionDeliveryEvidence; approved: boolean; reportAssetType: string;
        comment: string; expectedRevision: number } | null = null;
    readonly staleReview = signal(false);
    readonly reviewError = signal<string | null>(null);
    private groupsInitialized = false;

    readonly reportAssetTypeOptions = [
        { label: 'Completion evidence', value: 'COMPLETION_EVIDENCE' },
        { label: 'Signed delivery note', value: 'SIGNED_DELIVERY_NOTE' },
        { label: 'Dispatch note (from client)', value: 'CLIENT_DISPATCH_NOTE' },
        { label: 'Supporting document (reference only)', value: 'SUPPORTING_DOCUMENT' },
        { label: 'Not a report asset', value: 'NOT_REPORT_ASSET' }
    ];

    private readonly includeIncomplete = signal(false);

    get includeIncompleteSchedules(): boolean {
        return this.includeIncomplete();
    }

    set includeIncompleteSchedules(value: boolean) {
        this.includeIncomplete.set(!!value);
    }

    readonly latestReport = computed(() => this.reports()[0] || this.summary()?.latestReport || null);
    readonly canGenerate = computed(() => {
        const summary = this.summary();
        if (!summary || !this.canReconcileTasks() || this.loading() || this.generating()) return false;
        return this.includeIncomplete()
            ? !!summary.canGenerateIncludingIncomplete
            : !!summary.canGenerateCompletedOnly;
    });
    readonly generationReason = computed(() => {
        const summary = this.summary();
        if (!summary) return null;
        return this.includeIncomplete() ? summary.includingIncompleteReason : summary.completedOnlyReason;
    });

    ngOnInit(): void {
        this.load();
        this.loadGenerationJob();
    }

    ngOnDestroy(): void {
        this.jobPolling?.unsubscribe();
    }

    load(): void {
        const context = this.context();
        if (!context) return;
        this.loading.set(true);
        this.error.set(null);
        forkJoin({
            summary: this.posmService.getExecutionCompletionReportSummary(context.projectId, context.companyId),
            reports: this.posmService.getExecutionCompletionReports(context.projectId, context.companyId),
            evidence: this.posmService.getExecutionCompletionDeliveryEvidence(context.projectId, context.companyId)
        }).pipe(finalize(() => this.loading.set(false))).subscribe({
            next: ({ summary, reports, evidence }) => {
                this.summary.set(summary);
                this.reports.set([...(reports || [])].sort((left, right) => (right.version || 0) - (left.version || 0)));
                this.deliveryEvidence.set([...(evidence || [])].sort((left, right) => {
                    const delivery = (left.deliveryId || 0) - (right.deliveryId || 0);
                    return delivery || `${left.fileName || ''}`.localeCompare(`${right.fileName || ''}`);
                }));
                if (!this.groupsInitialized) {
                    const first = this.locationGroups()[0];
                    this.expandedLocations.set(new Set(first ? [first.key] : []));
                    this.expandedSchedules.set(new Set(first?.schedules[0] ? [first.schedules[0].key] : []));
                    this.groupsInitialized = true;
                }
            },
            error: error => {
                this.summary.set(null);
                this.reports.set([]);
                this.deliveryEvidence.set([]);
                this.error.set(error?.error?.message || 'Execution completion reports could not be loaded.');
            }
        });
    }

    generate(): void {
        const context = this.context();
        if (!context || !this.canGenerate()) return;
        this.generating.set(true);
        this.error.set(null);
        this.message.set(null);
        this.posmService.createExecutionCompletionReportJob(context.projectId, context.companyId, {
            includeIncompleteSchedules: this.includeIncomplete(),
            generatedByCustomerId: this.session.getCustomer()?.id
        }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: job => {
                this.generationJob.set(job);
                this.pollJob(job.id);
            },
            error: error => {
                this.generating.set(false);
                this.error.set(error?.error?.message || 'The execution completion report could not be queued.');
            }
        });
    }

    private loadGenerationJob(): void {
        const context = this.context();
        if (!context) return;
        this.posmService.latestExecutionCompletionReportJob(context.projectId, context.companyId)
            .pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: job => {
                this.generationJob.set(job);
                if (job && ['QUEUED', 'GENERATING'].includes(job.status)) {
                    this.generating.set(true);
                    this.pollJob(job.id);
                } else if (job?.status === 'FAILED') {
                    this.error.set(job.errorMessage || 'Report generation failed. Please try again.');
                }
            },
            error: () => this.error.set('Report generation status could not be loaded.')
        });
    }

    private pollJob(jobId: number): void {
        const context = this.context();
        if (!context) return;
        this.jobPolling?.unsubscribe();
        this.jobPolling = timer(0, 5000).pipe(switchMap(() =>
            this.posmService.getExecutionCompletionReportJob(context.projectId, jobId, context.companyId)))
            .subscribe({
                next: job => {
                    this.generationJob.set(job);
                    if (job.status === 'COMPLETED' || job.status === 'FAILED') {
                        this.jobPolling?.unsubscribe();
                        this.generating.set(false);
                        if (job.status === 'COMPLETED') {
                            this.message.set('Completion report generated.');
                            this.load();
                        } else {
                            this.error.set(job.errorMessage || 'Report generation failed. Please try again.');
                        }
                    }
                },
                error: () => {
                    this.generating.set(false);
                    this.error.set('Report status could not be refreshed. Reload this page to check again.');
                }
            });
    }

    download(report: PosmExecutionCompletionReport): void {
        const context = this.context();
        if (!context || !report.id) return;
        const actionKey = this.downloadActionKey(report);
        if (!this.reportActions.begin(actionKey)) return;
        this.error.set(null);
        this.posmService.downloadExecutionCompletionReport(context.projectId, report.id, context.companyId)
            .pipe(finalize(() => this.reportActions.end(actionKey)))
            .subscribe({
                next: blob => this.downloadBlob(blob, report.fileName || `posm-execution-completion-v${report.version || 1}.pdf`),
                error: error => this.error.set(error?.error?.message || 'The completion report could not be downloaded.')
            });
    }

    downloadActionKey(report: PosmExecutionCompletionReport): string {
        return `download:${report.id}`;
    }

    reviewEvidence(evidence: PosmExecutionCompletionDeliveryEvidence, approved: boolean): void {
        const context = this.context();
        const reviewer = this.session.getCustomer()?.id;
        if (!context || !reviewer || !evidence.sourceType || !evidence.sourceId || !evidence.deliveryScheduleId || evidence.mappingStatus !== 'MAPPED') {
            this.error.set('A POSM evidence approver is required to review this delivery image.');
            return;
        }
        const key = this.evidenceKey(evidence);
        const reportAssetType = approved ? evidence.reportAssetType
            : !evidence.reportAssetType || evidence.reportAssetType === 'UNCLASSIFIED' ? 'NOT_REPORT_ASSET' : evidence.reportAssetType;
        if (approved && (!reportAssetType || reportAssetType === 'UNCLASSIFIED' || reportAssetType === 'NOT_REPORT_ASSET')) {
            this.error.set('Select a report asset type before approving this delivery asset.');
            return;
        }
        this.reviewingEvidenceKey.set(key);
        this.error.set(null);
        this.posmService.reviewExecutionCompletionDeliveryEvidence(context.projectId, context.companyId,
            evidence.sourceType, evidence.sourceId, { approved, reportAssetType, reviewedByCustomerId: reviewer,
                deliveryScheduleId: evidence.deliveryScheduleId, expectedRevision: evidence.reviewRevision || 0, comment: evidence.reviewComment })
            .pipe(finalize(() => this.reviewingEvidenceKey.set(null)))
            .subscribe({
                next: reviewed => {
                    this.mergeEvidence(reviewed);
                    this.message.set(`Delivery asset ${approved ? 'approved and classified' : 'rejected'}.`);
                    this.load();
                },
                error: error => this.error.set(error?.error?.message || 'Delivery evidence could not be reviewed.')
            });
    }

    reconcileTasks(): void {
        const context = this.context();
        if (!context) return;
        this.reconcilingTasks.set(true);
        this.error.set(null);
        this.posmService.reconcileExecutionCompletionTasks(context.projectId, context.companyId)
            .pipe(finalize(() => this.reconcilingTasks.set(false)))
            .subscribe({
                next: result => {
                    this.message.set(`${result.completedTaskCount || 0} delivered POSM task(s) marked completed.`);
                    this.load();
                },
                error: error => this.error.set(error?.error?.message || 'Delivered POSM tasks could not be reconciled.')
            });
    }

    statusSeverity(status?: string | null): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        return `${status || ''}`.toUpperCase() === 'GENERATED' ? 'success' : 'info';
    }

    generatedBy(report: PosmExecutionCompletionReport): string {
        return report.generatedByActor?.name || (report.generatedByCustomerId ? `User #${report.generatedByCustomerId}` : '-');
    }

    evidenceKey(evidence: PosmExecutionCompletionDeliveryEvidence): string {
        return completionEvidenceKey(evidence);
    }

    toggleLocation(key: string): void { this.toggle(this.expandedLocations, key); }
    toggleSchedule(key: string): void { this.toggle(this.expandedSchedules, key); }
    private toggle(state: { (): Set<string>; set(value: Set<string>): void }, key: string): void {
        const keys = new Set(state()); keys.has(key) ? keys.delete(key) : keys.add(key); state.set(keys);
    }
    imageUnavailable(evidence: PosmExecutionCompletionDeliveryEvidence): void {
        const keys = new Set(this.unavailableImages()); keys.add(this.evidenceKey(evidence)); this.unavailableImages.set(keys);
    }
    reviewedBy(evidence: { reviewedByActor?: { name?: string | null }; reviewedByCustomerId?: number }): string {
        return evidence.reviewedByActor?.name || (evidence.reviewedByCustomerId ? `User #${evidence.reviewedByCustomerId}` : '-');
    }
    editReview(evidence: PosmExecutionCompletionDeliveryEvidence): void {
        if (!evidence.deliveryScheduleId || evidence.mappingStatus !== 'MAPPED') return;
        this.reviewDraft = { evidence: { ...evidence }, approved: evidence.approvalStatus !== 'REJECTED',
            reportAssetType: evidence.reportAssetType || 'UNCLASSIFIED', comment: evidence.reviewComment || '', expectedRevision: evidence.reviewRevision || 0 };
        this.staleReview.set(false); this.reviewError.set(null); this.reviewDialogVisible = true;
    }
    saveReview(): void {
        const context = this.context(); const draft = this.reviewDraft;
        if (!context || !draft?.evidence.deliveryScheduleId || !draft.evidence.sourceType || !draft.evidence.sourceId) return;
        const key = this.evidenceKey(draft.evidence);
        this.reviewingEvidenceKey.set(key); this.reviewError.set(null);
        this.posmService.reviewExecutionCompletionDeliveryEvidence(context.projectId, context.companyId,
            draft.evidence.sourceType, draft.evidence.sourceId, { deliveryScheduleId: draft.evidence.deliveryScheduleId,
                expectedRevision: draft.expectedRevision, approved: draft.approved,
                reportAssetType: draft.approved ? draft.reportAssetType : draft.reportAssetType === 'UNCLASSIFIED' ? 'NOT_REPORT_ASSET' : draft.reportAssetType,
                comment: draft.comment })
            .pipe(finalize(() => this.reviewingEvidenceKey.set(null))).subscribe({
                next: reviewed => { this.mergeEvidence(reviewed); this.reviewDialogVisible = false; this.reviewDraft = null;
                    this.message.set('Schedule asset review saved. Previous decisions remain in history.'); this.load(); },
                error: error => { this.staleReview.set(error?.status === 409);
                    this.reviewError.set(error?.error?.message || 'Review could not be saved. Your draft has been kept.'); }
            });
    }
    reloadReview(): void {
        const context = this.context(); const draft = this.reviewDraft;
        if (!context || !draft) return;
        this.posmService.getExecutionCompletionDeliveryEvidence(context.projectId, context.companyId).subscribe({
            next: rows => {
                const latest = rows.find(e => this.evidenceKey(e) === this.evidenceKey(draft.evidence));
                if (!latest || latest.mappingStatus !== 'MAPPED') { this.reviewError.set('This file is no longer mapped to the schedule.'); return; }
                this.reviewDraft = { ...draft, evidence: latest, expectedRevision: latest.reviewRevision || 0 };
                this.staleReview.set(false); this.reviewError.set('Latest review loaded below. Your draft is kept; check it before saving.');
            }, error: () => this.reviewError.set('Latest review could not be loaded. Your draft has been kept.')
        });
    }
    showHistory(evidence: PosmExecutionCompletionDeliveryEvidence): void {
        const context = this.context();
        if (!context || !evidence.deliveryScheduleId || !evidence.sourceType || !evidence.sourceId) return;
        this.historyAsset.set(evidence); this.reviewHistory.set([]); this.historyError.set(null); this.historyLoading.set(true);
        this.historyDialogVisible = true;
        this.posmService.getCompletionEvidenceReviewHistory(context.projectId, context.companyId, evidence.deliveryScheduleId,
            evidence.sourceType, evidence.sourceId).pipe(finalize(() => this.historyLoading.set(false))).subscribe({
                next: history => this.reviewHistory.set(history), error: () => this.historyError.set('Review history could not be loaded.')
            });
    }
    private mergeEvidence(evidence: PosmExecutionCompletionDeliveryEvidence): void {
        this.deliveryEvidence.update(rows => rows.map(row => this.evidenceKey(row) === this.evidenceKey(evidence) ? evidence : row));
    }

    evidenceSeverity(status?: string | null): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        switch (`${status || 'PENDING'}`.toUpperCase()) {
            case 'APPROVED': return 'success';
            case 'REJECTED': return 'danger';
            default: return 'warn';
        }
    }

    reportAssetTypeLabel(type?: string | null): string {
        return this.reportAssetTypeOptions.find(option => option.value === type)?.label || 'Unclassified';
    }

    tripTypeLabel(type?: string | null): string {
        const text = (type || 'LINKED_TRIP').replace(/_/g, ' ').toLowerCase();
        return text.charAt(0).toUpperCase() + text.slice(1);
    }

    isImageAsset(evidence: PosmExecutionCompletionDeliveryEvidence): boolean {
        const type = `${evidence.fileType || ''}`.toLowerCase();
        const name = `${evidence.fileName || evidence.fileUrl || ''}`.toLowerCase();
        return type.startsWith('image/') || type.includes('image') || type.includes('photo')
            || /\.(jpe?g|png|gif|webp|bmp)(?:[?#].*)?$/.test(name);
    }

    private context(): { projectId: number; companyId: number } | null {
        const project = this.project();
        const companyId = project?.owningCompanyId || this.session.getCompany()?.id;
        return project?.id && companyId ? { projectId: project.id, companyId } : null;
    }

    private downloadBlob(blob: Blob, fileName: string): void {
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        anchor.click();
        window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
    }
}
